import { AppointmentDetails } from "../types";
import { getAsync, isComposeMode, isOfficeContextAvailable } from "../utils";
import { getDuration, getAllAttendees } from "../utils";
import { CUSTOM_PROPERTIES } from "../constants";
import { Errors } from "../errors/AppError";

export class OutlookCalendarService {
  static async getAppointmentDetails(): Promise<AppointmentDetails> {
    if (!isOfficeContextAvailable()) {
      throw Errors.outlookUnavailable();
    }

    const item = Office.context.mailbox.item;

    if (!item) {
      throw Errors.outlookApiError("No active appointment");
    }

    return isComposeMode(item)
      ? this.getComposeDetails(item as Office.AppointmentCompose)
      : this.getReadDetails(item as Office.AppointmentRead);
  }

  static async updateLocation(location: string): Promise<void> {
    const item = Office.context.mailbox.item;

    if (!item?.location) {
      throw Errors.outlookApiError("Cannot update location");
    }

    return new Promise((resolve, reject) => {
      item.location.setAsync(location, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(Errors.outlookApiError(result.error?.message || "Failed to update location"));
        } else {
          resolve();
        }
      });
    });
  }

  static async addRoomAsAttendee(roomEmail: string, roomName?: string): Promise<void> {
    const item = Office.context.mailbox.item;

    if (!item?.requiredAttendees) {
      throw Errors.outlookApiError("Cannot add attendee");
    }

    const attendee = {
      displayName: roomName || roomEmail,
      emailAddress: roomEmail,
    } as Office.EmailAddressDetails;

    return new Promise((resolve, reject) => {
      item.requiredAttendees.addAsync([attendee], (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(Errors.outlookApiError(result.error?.message || "Failed to add attendee"));
        } else {
          resolve();
        }
      });
    });
  }

  static getDurationMinutes(start: Date, end: Date): number {
    return getDuration(start, end);
  }

  static formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} min`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h ${remainingMinutes}min`;
  }

  static getAttendeeCount(details: AppointmentDetails): number {
    return getAllAttendees(details).length;
  }

  static async storeBookingId(bookingId: string, roomId: string): Promise<void> {
    const item = Office.context.mailbox.item;

    if (!item?.loadCustomPropertiesAsync) {
      throw Errors.outlookApiError("Cannot store booking ID");
    }

    return new Promise((resolve, reject) => {
      item.loadCustomPropertiesAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(Errors.outlookApiError(result.error?.message || "Failed to load properties"));
          return;
        }

        const customProps = result.value;
        customProps.set(CUSTOM_PROPERTIES.BOOKING_ID, bookingId);
        customProps.set(CUSTOM_PROPERTIES.ROOM_ID, roomId);

        customProps.saveAsync((saveResult) => {
          if (saveResult.status === Office.AsyncResultStatus.Failed) {
            reject(Errors.outlookApiError(saveResult.error?.message || "Failed to save properties"));
          } else {
            resolve();
          }
        });
      });
    });
  }

  static async getStoredBookingInfo(): Promise<{ bookingId: string; roomId: string } | null> {
    const item = Office.context.mailbox.item;

    if (!item?.loadCustomPropertiesAsync) {
      return null;
    }

    return new Promise((resolve) => {
      item.loadCustomPropertiesAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          resolve(null);
          return;
        }

        const customProps = result.value;
        const bookingId = customProps.get(CUSTOM_PROPERTIES.BOOKING_ID) as string | undefined;
        const roomId = customProps.get(CUSTOM_PROPERTIES.ROOM_ID) as string | undefined;

        if (bookingId && roomId) {
          resolve({ bookingId, roomId });
        } else {
          resolve(null);
        }
      });
    });
  }

  static async clearStoredBookingInfo(): Promise<void> {
    const item = Office.context.mailbox.item;

    if (!item?.loadCustomPropertiesAsync) {
      return;
    }

    return new Promise((resolve) => {
      item.loadCustomPropertiesAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          resolve();
          return;
        }

        const customProps = result.value;
        customProps.remove(CUSTOM_PROPERTIES.BOOKING_ID);
        customProps.remove(CUSTOM_PROPERTIES.ROOM_ID);

        customProps.saveAsync(() => {
          resolve();
        });
      });
    });
  }

  private static async getComposeDetails(item: Office.AppointmentCompose): Promise<AppointmentDetails> {
    const [subject, start, end, location, requiredAttendees, optionalAttendees] = await Promise.all([
      getAsync<string>(item.subject.getAsync.bind(item.subject)),
      getAsync<Date>(item.start.getAsync.bind(item.start)),
      getAsync<Date>(item.end.getAsync.bind(item.end)),
      getAsync<string>(item.location.getAsync.bind(item.location)),
      getAsync<Office.EmailAddressDetails[]>(item.requiredAttendees.getAsync.bind(item.requiredAttendees)),
      getAsync<Office.EmailAddressDetails[]>(item.optionalAttendees.getAsync.bind(item.optionalAttendees)),
    ]);

    return {
      subject,
      start,
      end,
      location,
      organizer: Office.context.mailbox.userProfile.emailAddress,
      requiredAttendees: (requiredAttendees || []).map((a) => a.emailAddress),
      optionalAttendees: (optionalAttendees || []).map((a) => a.emailAddress),
      isOrganizer: true,
      mode: "compose",
    };
  }

  private static getReadDetails(item: Office.AppointmentRead): Promise<AppointmentDetails> {
    const userEmail = Office.context.mailbox.userProfile.emailAddress;

    return Promise.resolve({
      subject: item.subject || "",
      start: item.start,
      end: item.end,
      location: item.location || "",
      organizer: item.organizer?.emailAddress || "",
      requiredAttendees: (item.requiredAttendees || []).map((a) => a.emailAddress),
      optionalAttendees: (item.optionalAttendees || []).map((a) => a.emailAddress),
      itemId: item.itemId,
      isOrganizer: item.organizer?.emailAddress === userEmail,
      mode: "read",
    });
  }
}
